import get from 'lodash/get';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useMemo } from 'react';

import { enhance } from './enhance';
import Popup from '../common/Popup';
import Loader from '../common/Loader';
import { ACTIONS } from '../../constants/strings';
import SequentialSurvey from '../SequentialSurvey';
import { SURVEYS_STATUS_TYPE } from '../../constants';
import ExpandableListItem from '../ExpandableListItem';
import getSpecification from '../../utils/survey/getSpecification';
import { convertPreVisitFormData } from '../Surveys/utils/preVisitForm';

import styles from './index.styl';

const SurveysPopup = ({
  surveys,
  clearData,
  targetUserId,
  assignmentId,
  formatMessage,
  currentSurvey,
  isUpcomingSurveyRejected,
  sendUpcomingSurveyAnswers,
  sendUpcomingSurveyProceed,
  clearUpcomingSurveyCompletions,
  sendUpcomingSurveyCancelFilling,
  isSendingUpcomingSurveyCompletionsSucceed,
}) => {
  const [progress, setProgress] = useState(0);
  const [surveyIndex, setSurveyIndex] = useState(0);

  const stepper = useMemo(
    () => (
      <span>
        <span className={styles.stepper}>{`${surveyIndex + 1}`}</span>
        {`/${surveys.length}`}
      </span>
    ),
    [surveyIndex, surveys],
  );

  const status = get(currentSurvey, 'status');
  const surveyAssignmentId = get(surveys[surveyIndex], 'assignmentId');
  const intervieweeName = get(currentSurvey, 'interviewee.displayableName');

  const {
    name,
    heading,
    description,
    status: expirationStatus,
  } = getSpecification(currentSurvey, formatMessage, false);

  const userAppointment = get(currentSurvey, 'userAppointment', null);
  const list = convertPreVisitFormData(formatMessage, userAppointment);

  const succeed = get(currentSurvey, 'succeed');

  useEffect(() => {
    if (isUpcomingSurveyRejected) {
      clearData();
    }
  }, [isUpcomingSurveyRejected]);

  useEffect(() => {
    setSurveyIndex(0);
  }, [surveys]);

  useEffect(() => {
    if (surveyIndex === surveys.length) {
      clearData();
    }
  }, [surveyIndex, surveys]);

  useEffect(() => {
    if (surveyAssignmentId) {
      sendUpcomingSurveyProceed({
        targetUserId,
        assignmentId: surveyAssignmentId,
      });
    }
  }, [surveyIndex, surveyAssignmentId]);

  useEffect(() => {
    if (
      isSendingUpcomingSurveyCompletionsSucceed &&
      surveyIndex < surveys.length
    ) {
      clearUpcomingSurveyCompletions();
      setSurveyIndex(surveyIndex + 1);
    }
  }, [isSendingUpcomingSurveyCompletionsSucceed]);

  const handleSubmit = ({ id, value }) => {
    const body = {
      assignmentId: surveyAssignmentId,
      status: SURVEYS_STATUS_TYPE.IN_PROGRESS,
      token: get(currentSurvey, 'meta.token'),
    };

    if (value) {
      sendUpcomingSurveyAnswers({
        ...body,
        answers: {
          value,
          question_id: id,
        },
      });
    }
  };

  const handleClose = () => {
    if (status === SURVEYS_STATUS_TYPE.STARTED) {
      sendUpcomingSurveyCancelFilling({
        targetUserId,
        assignmentId,
        token: get(currentSurvey, 'meta.token'),
      });
    }
    clearData();
  };

  return (
    <Popup
      strictClose
      width="medium"
      overflowVisible
      onClose={handleClose}
      dialogClassName={styles.popup}
      opened={Boolean(surveyAssignmentId)}
      paperClassName={styles.paperClassName}
      dialogContetnClassName={styles.dialogContetnClassName}
      title={formatMessage(ACTIONS.survey.upcomingHeading, {
        user: intervieweeName,
      })}
    >
      <Choose>
        <When condition={surveyAssignmentId && succeed}>
          <ExpandableListItem
            locked
            key={assignmentId}
            progress={progress}
            data-id={assignmentId}
            header={{
              list,
              title: name,
              description,
              subtitle: heading,
              icon: 'up-surveys',
              note: expirationStatus,
              expirationStatus: stepper,
            }}
          >
            <SequentialSurvey
              survey={currentSurvey}
              onSubmit={handleSubmit}
              setProgress={setProgress}
            />
          </ExpandableListItem>
        </When>
        <Otherwise>
          <Loader />
        </Otherwise>
      </Choose>
    </Popup>
  );
};

export default enhance(SurveysPopup);

SurveysPopup.propTypes = {
  surveys: PropTypes.array,
  assignmentId: PropTypes.number,
  currentSurvey: PropTypes.object,
  clearData: PropTypes.func.isRequired,
  formatMessage: PropTypes.func.isRequired,
  isUpcomingSurveyRejected: PropTypes.bool,
  targetUserId: PropTypes.number.isRequired,
  sendUpcomingSurveyProceed: PropTypes.func.isRequired,
  sendUpcomingSurveyAnswers: PropTypes.func.isRequired,
  clearUpcomingSurveyCompletions: PropTypes.func.isRequired,
  isSendingUpcomingSurveyCompletionsSucceed: PropTypes.bool,
  sendUpcomingSurveyCancelFilling: PropTypes.func.isRequired,
};



// WEBPACK FOOTER //
// ./src/components/SurveysPopup/index.jsx